<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAppStore } from '@/stores/app.store.ts'
import { useAuthStore } from '@/stores/app/auth.store'
import ButtonBase from '@/components/button/ButtonBase.vue'
import { colors } from '@/globals/constants.ts'
import hash from 'string-hash'
import { bgColor500 } from '@/globals/tailwind.ts'
import { useRouter } from 'vue-router'
import type { User } from '@/types/model'
import type { ColorKey } from '@/types/tailwind'
import SkeletonBase from '@/components/skeleton/SkeletonBase.vue'

// router & route
const router = useRouter()

// stores
const { signOut } = useAuthStore()
const appStore = useAppStore()

// computed
const user = computed(() => appStore.user as User | null)
const initial = computed(() => getInitials(user.value?.name ?? 'User'))
const bgColor = computed(() => bgColor500[getColorForName(user.value?.name ?? 'User')])
const loaded = computed(() => appStore.loaded)

// refs
const showPopover = ref(false)

// methods
const togglePopover = () => (showPopover.value = !showPopover.value)

const closePopover = () => (showPopover.value = false)

const getColorForName = (name: string): ColorKey => {
  if (name === 'User') return 'neutral'
  const index = hash(name) % colors.length
  return colors[index] as ColorKey
}

const logout = () => {
  signOut()

  // refresh halaman
  router.push({ name: 'home' }).then(() => {
    window.location.reload()
  })
}

const getInitials = (name: string) => {
  const names = name.split(' ')
  if (names.length === 1) {
    return name.charAt(0)
  }
  return names[0].charAt(0) + names[1].charAt(0)
}
</script>

<template>
  <!-- Backdrop -->
  <Transition
    enter-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-200"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div v-if="showPopover" class="fixed inset-0 bg-black/50 z-40" @click="closePopover" />
  </Transition>

  <div v-if="loaded" class="relative z-50">
    <!-- Avatar button -->
    <div
      :class="bgColor"
      class="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs transition-colors duration-500 cursor-pointer"
      @click.stop="togglePopover"
    >
      {{ initial }}
    </div>

    <!-- Popover -->
    <Transition
      enter-active-class="transition-all duration-200"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-150"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div v-if="showPopover" class="absolute mt-2 right-0 w-54 bg-glass shadow-lg rounded-2xl p-3">
        <div class="space-y-2">
          <span
            class="block text-sm text-stone-900 dark:text-stone-100 font-semibold transition-colors duration-150"
            >Hai, {{ user?.name }}!</span
          >
          <span class="block text-xs text-stone-500">{{ user?.email }}</span>
          <div class="block text-right">
            <button-base @click="logout" size="sm">Logout</button-base>
          </div>
        </div>
      </div>
    </Transition>
  </div>
  <skeleton-base v-else width="9" height="9" rounded="full" color="stone"></skeleton-base>
</template>

<style scoped></style>
